<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['click', 'delete', 'rules', 'flow', 'quality']);
const { t } = useI18n();

// tradução com fallback PT fixo (sobrevive a chunk de locale velho em cache).
const tp = (key, fallback, args) => {
  const value = args ? t(key, args) : t(key);
  return value === key ? fallback : value;
};

const isActive = computed(() => props.agent.active);

// Identidade: a inicial do nome vira o avatar (Anna = A, Joana = J).
const initial = computed(() =>
  (props.agent.name || '?').trim().charAt(0).toUpperCase()
);

const inboxes = computed(() => props.agent.inboxes || []);

// Agentes do time que podem transferir conversas pra este (nó "Passar a
// outro agente"): sem caixa própria ele ainda atende por transferência.
const transferSources = computed(() => props.agent.transfer_sources || []);

// Ícone do canal de cada caixa (globinho pro site etc.) — padrão visual.
const CHANNEL_ICONS = {
  'Channel::WebWidget': 'i-lucide-globe',
  'Channel::Whatsapp': 'i-lucide-message-circle',
  'Channel::FacebookPage': 'i-lucide-facebook',
  'Channel::Instagram': 'i-lucide-instagram',
  'Channel::Email': 'i-lucide-mail',
  'Channel::Telegram': 'i-lucide-send',
  'Channel::Sms': 'i-lucide-message-square',
  'Channel::Api': 'i-lucide-plug',
};
const channelIcon = inbox =>
  CHANNEL_ICONS[inbox.channel_type] || 'i-lucide-inbox';

// Nome COMERCIAL do cérebro — nunca o modelo real (whitelabel):
// gpt-5.5 = Lia 5.1 (premium); gpt-5.6-luna = Lia Full-v3; o resto é Full-v2.
const modelLabel = computed(() => {
  if (!props.agent.model) return '';
  if (props.agent.model === 'gpt-5.5') return 'Lia 5.1';
  if (props.agent.model === 'gpt-6-luna') return t('LIA_FULL_V4_NAME');
  if (props.agent.model === 'gpt-5.6-luna') {
    return t('AI_AGENTS.FLOW.LIA.FULL_V3_NAME');
  }
  return t('AI_AGENTS.FLOW.LIA.FULL_NAME');
});

const rulesCount = computed(() => props.agent.agent_rules?.length || 0);

// Nota do Painel de Qualidade (média 7d do LLM-juiz); null = sem avaliações.
const qualityScore = computed(() => props.agent.quality_score);
const qualityClass = computed(() => {
  if (qualityScore.value >= 85) return 'bg-n-teal-3 text-n-teal-11';
  if (qualityScore.value >= 70) return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-ruby-3 text-n-ruby-11';
});
</script>

<template>
  <div
    class="group relative flex flex-col bg-n-solid-1 border border-n-weak rounded-2xl p-5 hover:border-n-brand/60 transition-colors duration-200 cursor-pointer"
    @click="emit('click', agent)"
  >
    <!-- Identidade + status -->
    <div class="flex items-start gap-3">
      <div
        class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-n-brand/25 to-n-brand/5 text-lg font-bold text-n-brand"
      >
        {{ initial }}
      </div>
      <div class="min-w-0 flex-1">
        <h3
          v-tooltip="agent.name"
          class="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold leading-snug text-n-slate-12"
        >
          {{ agent.name }}
        </h3>
        <p v-if="agent.sector" class="mt-0.5 text-xs text-n-slate-10">
          {{ agent.sector }}
        </p>
      </div>
      <span
        v-if="qualityScore >= 85"
        v-tooltip="
          tp(
            'AI_AGENTS.CARD.SEAL_TITLE',
            'Selo de qualidade: nota ≥85 no juiz de qualidade (7 dias)'
          )
        "
        class="inline-flex shrink-0 items-center rounded-full bg-n-teal-3 p-1 text-n-teal-11"
      >
        <span class="i-lucide-shield-check size-3.5" />
      </span>
      <span
        class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
        :class="
          isActive
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        <span
          class="size-1.5 rounded-full"
          :class="isActive ? 'bg-n-teal-9' : 'bg-n-slate-9'"
        />
        {{
          isActive
            ? t('AI_AGENTS.STATUS.ACTIVE')
            : t('AI_AGENTS.STATUS.INACTIVE')
        }}
      </span>
    </div>

    <!-- Descrição -->
    <p
      v-if="agent.description"
      class="mt-3 line-clamp-2 text-sm leading-relaxed text-n-slate-11"
    >
      {{ agent.description }}
    </p>

    <!-- Fatos: qualidade · modelo · voz · regras -->
    <div class="mt-3 flex flex-wrap items-center gap-1.5">
      <button
        v-if="qualityScore"
        type="button"
        class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
        :class="qualityClass"
        :title="
          tp(
            'AI_AGENTS.QUALITY.CHIP_TITLE',
            'Nota de qualidade (7 dias) — clique para detalhes'
          )
        "
        @click.stop="emit('quality', agent)"
      >
        <span class="i-lucide-gauge size-3" />
        {{ qualityScore }}
      </button>
      <span
        v-if="modelLabel"
        class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11"
      >
        <span class="i-lucide-cpu size-3" />
        {{ modelLabel }}
      </span>
      <span
        v-if="agent.tts_enabled"
        class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11"
      >
        <span class="i-lucide-audio-lines size-3" />
        {{ tp('AI_AGENTS.CARD.VOICE', 'Voz') }}
      </span>
      <span
        v-if="rulesCount > 0"
        class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11"
      >
        <span class="i-lucide-list-checks size-3" />
        {{
          tp('AI_AGENTS.CARD.RULES', '{count} regras', { count: rulesCount })
        }}
      </span>
    </div>

    <!-- Caixas atendidas: a informação que diz se o agente está DE FATO
         trabalhando; sem caixa = alerta, não silêncio -->
    <div class="mt-3 border-t border-n-weak pt-3 text-xs">
      <div
        v-if="inboxes.length"
        class="flex flex-wrap items-center gap-1.5 text-n-slate-11"
      >
        <span
          v-for="inbox in inboxes.slice(0, 2)"
          :key="inbox.id"
          class="inline-flex max-w-40 items-center gap-1 truncate rounded-md bg-n-alpha-1 px-1.5 py-0.5"
          :title="inbox.name"
        >
          <span :class="channelIcon(inbox)" class="size-3 shrink-0" />
          <span class="truncate">{{ inbox.name }}</span>
        </span>
        <span
          v-if="inboxes.length > 2"
          v-tooltip="
            inboxes
              .slice(2)
              .map(i => i.name)
              .join(', ')
          "
          class="cursor-default text-n-slate-10"
        >
          {{
            tp('AI_AGENTS.CARD.MORE_INBOXES', '+{count}', {
              count: inboxes.length - 2,
            })
          }}
        </span>
      </div>
      <div
        v-else-if="transferSources.length"
        class="flex items-center gap-1.5 text-n-teal-11"
      >
        <span class="i-lucide-arrow-right-left size-3.5" />
        {{
          tp(
            'AI_AGENTS.CARD.TEAM_TRANSFER',
            'Time de agentes — recebe transferências de {names}',
            { names: transferSources.join(', ') }
          )
        }}
      </div>
      <div v-else class="flex items-center gap-1.5 text-n-amber-11">
        <span class="i-lucide-inbox size-3.5" />
        {{
          tp(
            'AI_AGENTS.CARD.NO_INBOX',
            'Sem caixa vinculada — não atende ninguém'
          )
        }}
      </div>
    </div>

    <!-- Ações com nome (sem ícone-mistério) -->
    <div
      class="mt-auto flex items-center justify-between border-t border-n-weak pt-3"
    >
      <div class="flex items-center gap-1.5">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-workflow"
          :label="tp('AI_AGENTS.CARD.OPEN_CANVAS', 'Canvas')"
          @click.stop="emit('flow', agent)"
        />
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-list-checks"
          :label="tp('AI_AGENTS.CARD.OPEN_RULES', 'Regras')"
          @click.stop="emit('rules', agent)"
        />
      </div>
      <Button
        v-tooltip="t('AI_AGENTS.ACTIONS.DELETE')"
        icon="i-lucide-trash-2"
        variant="ghost"
        size="sm"
        color="ruby"
        @click.stop="emit('delete', agent)"
      />
    </div>
  </div>
</template>
